import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Link } from "@tanstack/react-router";
import { ProductCard } from "@/components/ProductCard";
import { listProducts } from "@/lib/catalog.functions";
import { topCategories } from "@/data/topCategories";
import pageCover from "@/assets/kupish/p-izdelki.webp";

const PAGE_SIZE = 20;

const searchSchema = z.object({
  cat: z.string().optional(),
  q: z.string().optional(),
  min: z.coerce.number().min(0).max(1000000).optional(),
  max: z.coerce.number().min(0).max(1000000).optional(),
  sort: z.enum(["new", "low", "high", "popular"]).optional(),
  page: z.coerce.number().int().min(1).max(500).optional(),
});

export const Route = createFileRoute("/izdelki/")({
  validateSearch: searchSchema,
  head: ({ match }) => {
    const base = "https://kupish.si/izdelki";
    const search = (match?.search ?? {}) as z.infer<typeof searchSchema>;
    const page = search.page && search.page > 1 ? search.page : 1;
    // Filtered/searched views must not compete with the clean listing.
    const isFiltered = Boolean(search.q || search.cat || search.min != null || search.max != null || (search.sort && search.sort !== "new"));
    const url = !isFiltered && page > 1 ? `${base}?page=${page}` : base;
    const title = !isFiltered && page > 1 ? `Vsi izdelki — stran ${page} | kupish.si` : "Vsi izdelki — kupish.si";
    const description = "Brskajte med novimi izdelki preverjenih ponudnikov — elektronika, dom in vrt, avto, moda, šport in drogerija na enem mestu.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: isFiltered ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        ...(!isFiltered && page > 1 ? [{ rel: "prev", href: page === 2 ? base : `${base}?page=${page - 1}` }] : []),
        { rel: "preload", as: "image", href: pageCover, fetchpriority: "high", type: "image/webp" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Vsi izdelki",
            description: "Spletno nakupovalno središče novih izdelkov različnih ponudnikov — elektronika, dom in vrt, avto, moda in šport.",
            url,
            inLanguage: "sl-SI",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
              { "@type": "ListItem", position: 2, name: "Vsi izdelki", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/izdelki/" });

  const [qInput, setQInput] = useState(search.q ?? "");
  const [minInput, setMinInput] = useState<string>(search.min != null ? String(search.min) : "");
  const [maxInput, setMaxInput] = useState<string>(search.max != null ? String(search.max) : "");

  useEffect(() => { setQInput(search.q ?? ""); }, [search.q]);

  const fetchProducts = useServerFn(listProducts);

  const page = search.page ?? 1;
  const { data, isLoading } = useQuery({
    queryKey: ["products", search],
    queryFn: () => fetchProducts({ data: {
      categorySlug: search.cat,
      q: search.q,
      priceMin: search.min,
      priceMax: search.max,
      sort: search.sort ?? "popular",
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    } }),
  });

  const list = data?.products ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const update = (patch: Partial<typeof search>, keepPage = false) => {
    navigate({ search: (prev: Record<string, unknown>) => {
      const next: Record<string, unknown> = { ...prev, ...patch };
      if (!keepPage) delete next.page;
      Object.keys(next).forEach((k) => {
        if (next[k] == null || next[k] === "") delete next[k];
      });
      return next;
    } });
  };

  const goToPage = (p: number) => {
    update({ page: p > 1 ? p : undefined }, true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyPrice = () => update({
    min: minInput ? Number(minInput) : undefined,
    max: maxInput ? Number(maxInput) : undefined,
  });

  const heroSrc = pageCover;
  const heroTitle = search.q ? `Iskanje: „${search.q}"` : "Vsi izdelki";
  const heroDesc = search.q
    ? `Rezultati iskanja po vseh izdelkih na kupish.si.`
    : "Brskajte med izdelki vseh ponudnikov — od elektronike do doma, vrta in športa.";

  return (
    <>
      <div className="relative overflow-hidden bg-card min-h-[250px] md:min-h-[270px]">
        {heroSrc && (
          <img
            src={heroSrc}
            alt=""
            aria-hidden
            width={1600}
            height={540}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-90 md:scale-[1.12]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 via-white/15 to-white/50" />

        <div className="relative z-10 px-4 pt-9 max-w-[88%] sm:px-6 md:max-w-[70%] md:px-10 md:pt-14">
          <h1 className="font-serif text-3xl leading-tight text-black sm:text-4xl md:text-5xl lg:text-6xl">
            {heroTitle}
          </h1>
          <p className="mt-3 max-w-[34ch] text-sm leading-snug text-black/85 md:mt-6 md:max-w-[40ch] md:text-base">
            {heroDesc}
          </p>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10 p-3 md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            {topCategories.map((t) => (
              <Link
                key={t.slug}
                to="/kategorije/$slug"
                params={{ slug: t.slug }}
                className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-accent/40 bg-background/90 backdrop-blur-sm px-3 py-2 text-xs text-foreground hover:border-accent hover:text-accent transition-colors sm:min-h-0 md:py-1.5"
              >
                {t.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

    <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <div className="mt-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <form onSubmit={(e) => { e.preventDefault(); update({ q: qInput || undefined }); }}
          className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex sm:flex-1 sm:min-w-[260px] sm:max-w-xl">
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Išči po izdelkih in trgovinah…"
            aria-label="Iskanje po izdelkih in trgovinah"
            maxLength={200}
            className="min-w-0 flex-1 bg-background border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-foreground"
          />
          <button type="submit" className="shrink-0 bg-primary text-primary-foreground px-4 py-2.5 text-sm hover:opacity-90 sm:px-5">
            Išči
          </button>
          {(search.q || search.min != null || search.max != null || search.cat) && (
            <button type="button" onClick={() => navigate({ search: {} })}
              className="col-span-2 shrink-0 border border-border px-4 py-2.5 text-sm hover:bg-muted sm:col-span-1">
              Počisti
            </button>
          )}
        </form>
        <div className="grid w-full gap-3 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-1 text-sm sm:flex">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">Cena</span>
            <input type="number" min="0" placeholder="od" value={minInput}
              aria-label="Najnižja cena"
              onChange={(e) => setMinInput(e.target.value)} onBlur={applyPrice}
              onKeyDown={(e) => e.key === "Enter" && applyPrice()}
              className="w-full min-w-0 bg-background border border-border px-2 py-1 text-sm sm:w-16" />
            <span>–</span>
            <input type="number" min="0" placeholder="do" value={maxInput}
              aria-label="Najvišja cena"
              onChange={(e) => setMaxInput(e.target.value)} onBlur={applyPrice}
              onKeyDown={(e) => e.key === "Enter" && applyPrice()}
              className="w-full min-w-0 bg-background border border-border px-2 py-1 text-sm sm:w-16" />
            <span className="text-muted-foreground text-xs">€</span>
          </div>
          <select value={search.sort ?? "popular"}
            aria-label="Razvrsti po"
            onChange={(e) => update({ sort: e.target.value as "new" | "low" | "high" | "popular" })}
            className="w-full min-w-0 bg-background border border-border px-3 py-2 text-sm sm:w-auto sm:py-1.5">
            <option value="popular">Najbolj priljubljeni</option>
            <option value="new">Najnovejše</option>
            <option value="low">Cena: nizka → visoka</option>
            <option value="high">Cena: visoka → nizka</option>
          </select>
        </div>
      </div>


      {isLoading ? (
        <div className="mt-16 text-center text-muted-foreground py-16">Nalagam…</div>
      ) : list.length === 0 ? (
        <div className="mt-16 text-center text-muted-foreground py-16 border border-dashed border-border">
          Ni zadetkov za izbrane filtre.
        </div>
      ) : (
        <>
          <div className="mt-6 text-xs text-muted-foreground">
            Prikazano {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} od {total}
          </div>
          <h2 className="sr-only">Seznam izdelkov</h2>
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4 mt-4">
            {list.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          {totalPages > 1 && (
            <div className="mt-12 flex flex-col items-center justify-center gap-3">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Stran {page} od {totalPages}
              </span>
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => goToPage(page - 1)} disabled={page <= 1}
                  className="shrink-0 whitespace-nowrap px-3 py-2 text-sm border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed">
                  ← Prejšnja
                </button>
                <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                  className="shrink-0 whitespace-nowrap px-3 py-2 text-sm border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed">
                  Naslednja →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
    </>
  );
}
